/**
 * Gestionnaire d'erreurs centralisé
 * Fournit des fonctions utilitaires pour gérer et formater les erreurs
 */

/**
 * Formate un message d'erreur lisible pour l'utilisateur
 * @param {Error} error - L'erreur à formater
 * @returns {string} Message d'erreur formaté
 */
export const formatErrorMessage = (error) => {
  // Si c'est déjà un message d'erreur formaté
  if (typeof error === 'string') {
    return error;
  }

  // Erreur réseau
  if (error.isNetworkError || error.message?.includes('Network Error') || error.message?.includes('fetch')) {
    return 'Erreur de connexion. Veuillez vérifier votre connexion internet.';
  }

  // Erreur de timeout
  if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
    return 'La requête a pris trop de temps. Veuillez réessayer.';
  }

  // Erreur 401 - Non autorisé
  if (error.status === 401) {
    return 'Votre session a expiré. Veuillez vous reconnecter.';
  }

  // Erreur 403 - Accès refusé (message serveur prioritaire)
  if (error.status === 403) {
    if (error.data?.message && typeof error.data.message === 'string') {
      return error.data.message;
    }
    return 'Vous n\'avez pas les permissions nécessaires pour effectuer cette action.';
  }

  // Erreur 404 - Non trouvé
  if (error.status === 404) {
    return 'La ressource demandée est introuvable.';
  }

  // Erreur 500 - Erreur serveur
  if (error.status === 500) {
    return 'Une erreur serveur est survenue. Veuillez réessayer plus tard.';
  }

  // Message d'erreur du serveur
  if (error.data?.message) {
    return error.data.message;
  }

  // Message d'erreur standard
  if (error.message) {
    return error.message;
  }

  // Message par défaut
  return 'Une erreur inattendue est survenue. Veuillez réessayer.';
};

/**
 * Log une erreur de manière structurée (pour le debugging)
 * @param {Error} error - L'erreur à logger
 * @param {string} context - Le contexte où l'erreur s'est produite
 */
export const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[ERROR] ${context}:`, {
      message: error.message,
      status: error.status,
      data: error.data,
      stack: error.stack,
    });
  }
};

/**
 * Vérifie si une erreur est une erreur réseau
 * @param {Error} error - L'erreur à vérifier
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return error.isNetworkError || 
         error.message?.includes('Network Error') || 
         error.message?.includes('fetch') ||
         !error.status;
};

/**
 * Vérifie si une erreur est une erreur d'authentification
 * @param {Error} error - L'erreur à vérifier
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  return error.status === 401 || error.status === 403;
};

/**
 * Gère une erreur de manière centralisée
 * @param {Error} error - L'erreur à gérer
 * @param {string} context - Le contexte où l'erreur s'est produite
 * @returns {string} Message d'erreur formaté pour l'utilisateur
 */
export const handleError = (error, context = '') => {
  logError(error, context);
  return formatErrorMessage(error);
};

export default {
  formatErrorMessage,
  logError,
  isNetworkError,
  isAuthError,
  handleError,
};

